/** Decode a JWT payload for client-side continuity and expiry heuristics. */
export function readJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const claims = JSON.parse(base64UrlDecode(parts[1])) as unknown;
    if (!claims || typeof claims !== 'object' || Array.isArray(claims)) return null;
    return claims as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Read a token's server session id without treating it as verified authority. */
export function readJwtSessionId(token: string | null): string | null {
  if (!token) return null;
  const claims = readJwtClaims(token);
  return typeof claims?.sid === 'string' && claims.sid !== '' ? claims.sid : null;
}

/** Client-side account identity hint carried by an access or refresh JWT. */
export interface JwtAccountIdentity {
  orgId: string;
  userId: string;
}

/**
 * Read the signed account claims without treating this browser-side decode as
 * authorization. The gateway still verifies every token; the PWA uses this only
 * to bind account-scoped recovery records to the same user and organization.
 */
export function readJwtAccountIdentity(token: string | null): JwtAccountIdentity | null {
  if (!token) return null;
  const claims = readJwtClaims(token);
  const orgId = typeof claims?.org_id === 'string' ? claims.org_id.toLowerCase() : null;
  const userId = typeof claims?.sub === 'string' ? claims.sub : null;
  return orgId && userId ? { orgId, userId } : null;
}

/** Read a token's expiry in seconds without treating it as verified authority. */
export function readJwtExpSeconds(token: string): number | null {
  const claims = readJwtClaims(token);
  return typeof claims?.exp === 'number' ? claims.exp : null;
}

/** Decode base64url data as UTF-8. */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
